import cx from "classnames";
import React, { useMemo } from "react";
import type { ActiveAnalysis } from "../../../../../../../../api/useActiveAnalysesStream";
import type { Project } from "../../../../../../../../api/useProjects";
import { useSetups } from "../../../../../../../../api/useSetups";
import type { Source } from "../../../../../../../../api/useSources";
import { useCanUserDoOnProject } from "../../../../../../../canUserDo";
import t from "../../../../../../../../i18n";
import type { NonEmptyArray } from "diagnostics-lib/array";
import ChartCard from "@ui/Highcharts/ui/Card";
import Selector from "@ui/Selector";
import ActionMenu from "../../../../../../Analysis/AnalysisActionMenu";
import PeriodSelector from "../../../FaultEvent/components/PeriodSelector";
import ToggleGroupBreaks from "../../../../../../Analysis/ToggleGroupBreaks";
import InfoTooltip from "../../../../../../Chart/InfoTooltip";
import type { SelectedQuantity } from "../../../../../../useSelectedQuantities";
import type { SymptomChartType } from "../../../../../../Symptoms/SymptomChartType";
import TogglePublicationDisplay, {
  usePublicationFlagDisplay,
} from "../../../../../../Analysis/TogglePublicationDisplay";
import { ChartMenu } from "../../../../../../Chart/Menu";
import { useBreaksBetweenGroups } from "../../../../../../Analysis/useBreaksBetweenGroups";
import { FullViewportButtonMarker, FullViewportTrigger } from "@ui/FullViewportButton";
import { useSettings } from "../../../../../../../../api/useSettings";
import { css } from "@linaria/core";
import { spacings } from "@ui/Theme";
import ToggleDisplayInvalidPoints, {
  useDisplayInvalidPoints,
} from "../../../../../../Analysis/ToggleDisplayInvalidPoints";
import {
  ExportWidgetButton,
  exportWidgetTarget,
  HIDE_ON_WIDGET_EXPORT,
} from "@ui/ExportWidgetButton";
import { useEventPeriod } from "../../../../../hooks/useEventPeriod";
import Button from "@ui/Button";
import { RefreshLine } from "@ui/IconV2/react";
import Text from "@ui/Text";
import { generateExportFileName } from "diagnostics-lib/string";
import type { Event } from "../../../../../../../../api/useEvents";
import useActiveAnalysesWithDisplayFilters from "../../../../../../../../api/useActiveAnalysesWithDisplayFilters";
import SymptomHighCharts from "./SymptomHighCharts";
import EvolutionSubHeader from "../../../../../../EvolutionSubHeader";
import { DisplayFiltersBlankState } from "../../../../../../EvolutionSubHeader/DisplayFiltersBlankState";

interface P {
  project: Project;
  source: Source;
  activeAnalyses: ActiveAnalysis[];
  selectedQuantities: NonEmptyArray<SelectedQuantity>;
  selectedChartOption: SymptomChartType;
  setSelectedChartOption: (option: SymptomChartType) => void;
  event: Event;
}

const SymptomEvolution = ({
  project,
  source,
  activeAnalyses,
  selectedQuantities: selectedQuantitiesState,
  selectedChartOption,
  setSelectedChartOption,
  event,
}: P) => {
  const projectId = project.id;
  const setups = useSetups(project.id);

  const settings = useSettings();
  const { displayFilterRemovesAllActiveAnalyses } = useActiveAnalysesWithDisplayFilters(project.id);

  const canUserWriteProject = useCanUserDoOnProject("PROJECT_WRITE", projectId);
  const canUserReadProject = useCanUserDoOnProject("PROJECT_READ", project.id);

  const [_, __, ___, currentPeriodIsDefaultPeriod, setPeriodToDefault, ____] = useEventPeriod({
    event,
    source,
    project,
    analyses: activeAnalyses,
    boundaries: "active-analyses",
  });

  const [breakBetweenGroups] = useBreaksBetweenGroups({
    scope: projectId,
  });
  const [publicationFlagDisplayed] = usePublicationFlagDisplay({
    scope: projectId,
    defaultValue: settings.DISPLAY_PUBLICATION_MARKERS,
  });
  const [displayInvalidPoints] = useDisplayInvalidPoints({
    scope: projectId,
  });

  const chartTypeOptions = React.useMemo(
    () => [
      {
        value: "amplitude" as const,
        label: t("symptom.chart.header.option.amplitude"),
        description: t("symptom.chart.header.option.amplitude.description"),
      },
      {
        value: "gap" as const,
        label: t("symptom.chart.header.option.gap"),
        description: t("symptom.chart.header.option.gap.description"),
      },
      {
        value: "normalizedGap" as const,
        label: t("symptom.chart.header.option.normalizedGap"),
        description: t("symptom.chart.header.option.normalizedGap.description"),
      },
    ],
    [],
  );
  const selectedQuantities: NonEmptyArray<SelectedQuantity> = React.useMemo(
    () =>
      selectedChartOption === "normalizedGap"
        ? selectedQuantitiesState
        : [selectedQuantitiesState[0]],
    [selectedChartOption, selectedQuantitiesState],
  );

  const subtitle = React.useMemo(
    () => (
      <EvolutionSubHeader
        projectIdToFilter={projectId}
        allAnalysesMode={false}
        chartOptions={
          <InfoTooltip
            title={t("symptom.chart.header.option.title")}
            options={chartTypeOptions}
            link="/symptoms-page"
          >
            <div className={HIDE_ON_WIDGET_EXPORT}>
              <Selector
                options={chartTypeOptions}
                value={selectedChartOption}
                onChange={setSelectedChartOption}
              />
            </div>
          </InfoTooltip>
        }
        periodSettings={
          <>
            {!currentPeriodIsDefaultPeriod && (
              <Button variant="tertiary" onClick={setPeriodToDefault} leftIcon={<RefreshLine />}>
                {t("project.fault_event.fault.chart.period_picker.reset")}
              </Button>
            )}
            <PeriodSelector
              event={event}
              source={source}
              project={project}
              analyses={activeAnalyses}
              boundaries="active-analyses"
            />
          </>
        }
        visibilitySettings={
          <ChartMenu
            marker={!breakBetweenGroups || publicationFlagDisplayed || displayInvalidPoints}
            tooltipOverlay={t("chart.display_options.tooltip")}
          >
            <ToggleDisplayInvalidPoints projectId={projectId} />
            <TogglePublicationDisplay projectId={project.id} />
            <ToggleGroupBreaks projectId={projectId} groupables={activeAnalyses} />
          </ChartMenu>
        }
        actionMenu={
          !displayFilterRemovesAllActiveAnalyses && (
            <ActionMenu
              event={event}
              analyses={activeAnalyses}
              project={project}
              canWriteProject={canUserWriteProject}
              canUserReadProject={canUserReadProject}
              allAnalysesMode={false}
              className={HIDE_ON_WIDGET_EXPORT}
            />
          )
        }
      />
    ),
    [
      projectId,
      chartTypeOptions,
      selectedChartOption,
      setSelectedChartOption,
      currentPeriodIsDefaultPeriod,
      setPeriodToDefault,
      event,
      source,
      project,
      activeAnalyses,
      breakBetweenGroups,
      publicationFlagDisplayed,
      displayInvalidPoints,
      displayFilterRemovesAllActiveAnalyses,
      canUserWriteProject,
      canUserReadProject,
    ],
  );
  const title = useMemo(
    () => (
      <>
        <Text as="span" type="h4">
          {t("symptoms.chart.title")}
        </Text>
        <div style={{ marginLeft: "auto" }}>
          <ExportWidgetButton
            fileName={generateExportFileName({
              filename: t("symptoms.chart.title"),
              projectName: project.name,
              extension: "png",
            })}
          />
          <FullViewportTrigger style={{ marginLeft: spacings.xs }} />
        </div>
      </>
    ),
    [project.name],
  );
  if (displayFilterRemovesAllActiveAnalyses) {
    return (
      <ChartCard
        title={title}
        subtitle={subtitle}
        className={cx(symptomEvolutionChart, FullViewportButtonMarker, exportWidgetTarget)}
      >
        <DisplayFiltersBlankState projectId={projectId} />
      </ChartCard>
    );
  }

  return (
    <SymptomHighCharts
      activeAnalyses={activeAnalyses}
      project={project}
      selectedChartOption={selectedChartOption}
      selectedQuantities={selectedQuantities}
      setups={setups}
      source={source}
      subtitle={subtitle}
      title={title}
      event={event}
    />
  );
};

export default React.memo(SymptomEvolution);

//#region CSS
const symptomEvolutionChart = css`
  row-gap: ${spacings.m};
`;
//#endregion CSS
